import type { FormEvent, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { RELATIONSHIP_TYPE_OPTIONS, type ConnectedHorse, type Relationship } from '../mockData.js';
import type { EndOrReplaceValues, RelationshipDraft, RelationshipEditorValues, RelationshipEndDraft } from '../reviewState.js';

function useDialog(open: boolean) {
  const ref = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return ref;
}

export interface RelationshipEditorDialogProps {
  open: boolean;
  draft: RelationshipDraft;
  horses: ConnectedHorse[];
  dirty: boolean;
  error: string;
  result: string;
  onClose: () => void;
  onSave: (returnAfter: boolean) => void;
  onChange: (patch: Partial<RelationshipEditorValues>) => void;
  onDiscard: () => void;
}

export function RelationshipEditorDialog({ open, draft, horses, dirty, error, result,
  onClose, onSave, onChange, onDiscard }: RelationshipEditorDialogProps): ReactNode {
  const dialogRef = useDialog(open), titleId = useId(), values = draft.values;
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); onSave(false); };
  const field = (key: keyof RelationshipEditorValues, label: string, type = 'text') => <label>{label}
    <input name={key} type={type} autoComplete="off" value={values[key]} onChange={event => onChange({ [key]: event.target.value })} />
  </label>;
  return <dialog className="relationship-dialog" ref={dialogRef} aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }} onClose={onClose}>
    <header><h2 id={titleId}>{draft.mode === 'add' ? 'Add relationship' : 'Edit relationship'}</h2>
      <button type="button" aria-label="Close relationship editor" onClick={onClose}>×</button></header>
    <form onSubmit={submit}>
      <div className="rel-form-grid">
        <label>Relationship type<select name="type" value={values.type} onChange={event => onChange({ type: event.target.value as RelationshipEditorValues['type'] })}>
          {RELATIONSHIP_TYPE_OPTIONS.map(option => <option key={option}>{option}</option>)}
        </select></label>
        <label>Linked horse<select name="horseId" value={values.horseId} onChange={event => onChange({ horseId: event.target.value })}>
          {horses.map(horse => <option key={horse.id} value={horse.id}>{horse.name}</option>)}
        </select></label>
      </div>
      {field('specialty', 'Role or specialty')}
      {field('organization', 'Organization or practice')}
      {field('contact', 'Contact name')}
      <div className="rel-form-grid">{field('location', 'Location')}{field('startDate', 'Effective date', 'date')}</div>
      <label>Relationship notes<textarea name="notes" value={values.notes} onChange={event => onChange({ notes: event.target.value })} /></label>
      {error && <p className="relationship-editor-error" role="alert">{error}</p>}
      {result && <p className="relationship-editor-result" role="status">{result}</p>}
      <p className="dialog-note">Design preview only. Saved records and unsaved drafts stay in this review until reload.
        Recording a connection does not verify its identity or authorize outreach.</p>
      <div className="dialog-actions">
        <button type="button" className="outlined" onClick={onClose}>Close</button>
        <button type="button" className="outlined" disabled={!dirty} onClick={onDiscard}>Discard unsaved changes</button>
        <button type="submit" className="blue-action">Save</button>
        <button type="button" className="blue-action" onClick={() => onSave(true)}>Save &amp; return</button>
      </div>
    </form>
  </dialog>;
}

export interface EndOrReplaceDialogProps {
  open: boolean;
  target: Relationship;
  draft: RelationshipEndDraft;
  linkedHorseName: string;
  dirty: boolean;
  error: string;
  onClose: () => void;
  onConfirm: () => void;
  onChange: (patch: Partial<EndOrReplaceValues>) => void;
  onDiscard: () => void;
}

export function EndOrReplaceDialog({ open, target, draft, linkedHorseName, dirty, error,
  onClose, onConfirm, onChange, onDiscard }: EndOrReplaceDialogProps): ReactNode {
  const dialogRef = useDialog(open), titleId = useId(), values = draft.values;
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); onConfirm(); };
  return <dialog className="relationship-dialog" ref={dialogRef} aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }} onClose={onClose}>
    <header><h2 id={titleId}>End or replace connection</h2>
      <button type="button" aria-label="Close end editor" onClick={onClose}>×</button></header>
    <form onSubmit={submit}>
      <p className="dialog-note">{target.organization || target.contact} · {target.specialty} · {linkedHorseName}</p>
      <label>Change occurred on<input name="endedDate" type="date" value={values.endedDate}
        onChange={event => onChange({ endedDate: event.target.value })} /></label>
      <label>Reason or notes<textarea name="reason" value={values.reason}
        onChange={event => onChange({ reason: event.target.value })} /></label>
      <label>What happens next?<select name="next" value={values.next}
        onChange={event => onChange({ next: event.target.value as EndOrReplaceValues['next'] })}>
        <option value="end">End this connection only</option>
        <option value="replace">End this connection and add a replacement</option>
      </select></label>
      {error && <p className="relationship-editor-error" role="alert">{error}</p>}
      <p className="dialog-note">Only this connection moves to Former. Other care team members stay current.
        Previous facts remain in history. This review does not update the CRM or send outreach.</p>
      <div className="dialog-actions">
        <button type="button" className="outlined" onClick={onClose}>Close</button>
        <button type="button" className="outlined" disabled={!dirty} onClick={onDiscard}>Discard unsaved changes</button>
        <button type="submit" className="blue-action">Confirm end</button>
      </div>
    </form>
  </dialog>;
}
